import { Check, Heart, HeartOff, Info, Pencil, Plus, Star, StarOff, Trash2 } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuLabel } from './dropdown-menu.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface ViewOptionsMenuProps {
  hasActiveView: boolean;
  isDirty: boolean;
  canSaveAsNew: boolean;
  onSave: () => void;
  onSaveAsNew: () => void;
  onRename: () => void;
  onDelete: () => void;
  /** Omit on surfaces without Favorites (the Signals queue) to hide the entry entirely. */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  /** Whether the active View feeds the Feed; pass onToggleWatch only for a Static View. */
  isWatched?: boolean;
  onToggleWatch?: () => void;
  /** A Following View: always followed, never renamed or deleted. */
  systemView?: boolean;
  /** Why this View cannot be followed (a filter View), shown as a disabled Follow view. */
  followUnavailable?: string;
}

export function ViewOptionsMenu({
  hasActiveView,
  isDirty,
  canSaveAsNew,
  onSave,
  onSaveAsNew,
  onRename,
  onDelete,
  isFavorite = false,
  onToggleFavorite,
  isWatched = false,
  onToggleWatch,
  systemView = false,
  followUnavailable,
}: ViewOptionsMenuProps) {
  return (
    <>
      {hasActiveView && onToggleFavorite ? (
        <>
          <DropdownMenuItem onSelect={onToggleFavorite}>
            {isFavorite ? <HeartOff aria-hidden /> : <Heart aria-hidden />}
            {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </DropdownMenuItem>
        </>
      ) : null}
      {hasActiveView && systemView ? (
        <DropdownMenuItem disabled>
          <Check aria-hidden />
          Following view
        </DropdownMenuItem>
      ) : hasActiveView && onToggleWatch ? (
        <DropdownMenuItem onSelect={onToggleWatch}>
          {isWatched ? <StarOff aria-hidden /> : <Star aria-hidden />}
          {isWatched ? 'Unfollow view' : 'Follow view'}
        </DropdownMenuItem>
      ) : hasActiveView && followUnavailable ? (
        // Not disabled, so the reason's tooltip still opens on hover; selecting it does nothing.
        <DropdownMenuItem
          aria-disabled
          className="text-muted-foreground focus:text-muted-foreground"
          onSelect={(event) => event.preventDefault()}
        >
          <Star aria-hidden />
          Follow view
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-auto inline-flex" aria-label={followUnavailable}>
                  <Info aria-hidden className="size-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent>{followUnavailable}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DropdownMenuItem>
      ) : null}
      {hasActiveView && isDirty ? (
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
          This view has unsaved changes
        </DropdownMenuLabel>
      ) : null}
      {hasActiveView && isDirty ? (
        <DropdownMenuItem onSelect={onSave}>
          <Check aria-hidden />
          Save
        </DropdownMenuItem>
      ) : null}
      {canSaveAsNew || !hasActiveView ? (
        <DropdownMenuItem disabled={!canSaveAsNew} onSelect={onSaveAsNew}>
          <Plus aria-hidden />
          Save as new view
        </DropdownMenuItem>
      ) : null}
      {hasActiveView && !systemView ? (
        <>
          <DropdownMenuItem onSelect={onRename}>
            <Pencil aria-hidden />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
            <Trash2 aria-hidden />
            Delete
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  );
}
